import { useQuery } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
import { gql, request } from 'graphql-request'
import { type Address, useBaseReads } from 'use-towns-client'
import { getAddress } from 'viem'
import { z } from 'zod'

const BOT_REVIEWS_QUERY = gql`
    query GetBotReviews($app: String!) {
        agentFeedbacks(where: { app: $app }, orderBy: "createdAt", orderDirection: "desc") {
            items {
                comment
                rating
                reviewerAddress
                createdAt
            }
        }
        agentReputationSummary(app: $app) {
            activeFeedback
            totalFeedback
            averageRating
        }
    }
`

/**
 * Parse comment data URI format: data:application/json,%7B%22text%22%3A%22...%22%2C%22timestamp%22%3A...%7D
 * Returns { text, timestamp } where timestamp is in milliseconds
 */
const parseCommentDataUri = (
    comment: string,
): { text: string; timestampMs: number | undefined } => {
    try {
        if (comment.startsWith('data:application/json,')) {
            const encoded = comment.slice('data:application/json,'.length)
            const decoded = decodeURIComponent(encoded)
            const parsed = JSON.parse(decoded)
            return {
                text: parsed.text || comment,
                timestampMs: parsed.timestamp, // Already in ms from the comment
            }
        }
    } catch {
        // Fall back to raw comment if parsing fails
    }
    return { text: comment, timestampMs: undefined }
}

/**
 * Convert 0-100 rating scale to 0-5 star scale
 */
const normalizeRating = (rating: number): number => {
    return Math.max(0, Math.min(5, rating / 20))
}

const agentFeedbackItemSchema = z
    .object({
        comment: z.string().nullable(),
        rating: z.coerce.number().nullable(),
        reviewerAddress: z.string().nullable(),
        createdAt: z.coerce.number().nullable(),
    })
    .transform((item) => {
        const { text, timestampMs } = parseCommentDataUri(item.comment ?? '')
        // createdAt from subgraph is in seconds, convert to ms
        // Fall back to timestamp from comment if createdAt is missing
        const createdAtMs = item.createdAt ? item.createdAt * 1000 : timestampMs
        return {
            text,
            rating: normalizeRating(item.rating ?? 0),
            reviewerAddress: item.reviewerAddress ?? '',
            timestamp: createdAtMs,
        }
    })

const botReviewsResponseSchema = z.object({
    agentFeedbacks: z
        .object({
            items: z.array(agentFeedbackItemSchema),
        })
        .nullable(),
    agentReputationSummary: z
        .object({
            activeFeedback: z.coerce.number().nullable(),
            totalFeedback: z.coerce.number().nullable(),
            averageRating: z.coerce.number().nullable(),
        })
        .nullable()
        .transform((summary) =>
            summary
                ? {
                      activeFeedback: summary.activeFeedback ?? 0,
                      totalFeedback: summary.totalFeedback ?? 0,
                      averageRating: normalizeRating(summary.averageRating ?? 0),
                  }
                : null,
        ),
})

export type BotReview = z.infer<typeof agentFeedbackItemSchema>

export type BotReviewSummary = {
    activeFeedback: number
    totalFeedback: number
    averageRating: number // 0-5 scale
}

export type BotReviewsResponse = {
    success: boolean
    reviews: BotReview[]
    summary: BotReviewSummary | null
    totalCount: number
}

const fetchBotReviews = async (
    appAddress: Address | undefined,
    subgraphEndpoint: string | undefined,
): Promise<BotReviewsResponse> => {
    if (!appAddress || !subgraphEndpoint) {
        return {
            success: false,
            reviews: [],
            summary: null,
            totalCount: 0,
        }
    }

    try {
        // Normalize to checksummed format to match subgraph storage
        const rawData = await request(subgraphEndpoint, BOT_REVIEWS_QUERY, {
            app: getAddress(appAddress),
        })

        const parsed = botReviewsResponseSchema.safeParse(rawData)
        if (!parsed.success) {
            console.error('[useBotReviews] Invalid response schema:', parsed.error)
            return {
                success: false,
                reviews: [],
                summary: null,
                totalCount: 0,
            }
        }

        const data = parsed.data
        const reviews = data.agentFeedbacks?.items ?? []
        const summary = data.agentReputationSummary

        return {
            success: true,
            reviews,
            summary,
            totalCount: summary?.totalFeedback ?? reviews.length,
        }
    } catch (error) {
        console.error('[useBotReviews] Error fetching bot reviews:', error)
        return {
            success: false,
            reviews: [],
            summary: null,
            totalCount: 0,
        }
    }
}

export const useBotReviews = (appAddress: Address | undefined) => {
    const { subgraphEndpoint } = useBaseReads()

    return useQuery({
        queryKey: ['bot-reviews', appAddress],
        queryFn: () => fetchBotReviews(appAddress, subgraphEndpoint),
        enabled: !!appAddress && !!subgraphEndpoint,
        staleTime: MINUTE_MS * 5,
        refetchOnWindowFocus: false,
    })
}
